import React, { useState, useCallback, useMemo } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useModalStore, useAuthStore } from '@/store';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { formatCreditBalance, resolveComposerCreditQuote } from '@/lib/formatCredits';
import { FILE_CREDIT_NOTE } from '@/components/ChatInput/ChatInputCreditNote';
import PlusOutOfCreditsOptions from '@/components/Credits/PlusOutOfCreditsOptions';

const createStyles = (colors) => StyleSheet.create({
  dropdownContent: {
    padding: 20,
    minWidth: '100%',
    maxWidth: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 15,
    paddingBottom: 10,
  },
  creditsIconContainer: {
    width: 45,
    height: 45,
    borderRadius: 25,
    backgroundColor: colors.accent_color_primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTextContainer: {
    flex: 1,
  },
  creditsNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text_color_primary,
    marginBottom: 2,
  },
  title: {
    fontSize: 13,
    color: colors.text_color_secondary,
    textTransform: 'capitalize',
    letterSpacing: 0.25,
  },
  infoSection: {
    marginBottom: 25,
    minWidth: 210,
  },
  costStack: {
    gap: 8,
  },
  costContainer: {
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    padding: 12,
  },
  costContainerWarning: {
    backgroundColor: colors.color_destructive + '15',
  },
  costRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  costCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  costLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text_color_primary,
  },
  costHint: {
    fontSize: 12,
    lineHeight: 16,
    color: colors.text_color_secondary,
  },
  costValue: {
    fontSize: 14,
    color: colors.text_color_primary,
    fontWeight: '700',
  },
  costValueWarning: {
    color: colors.color_destructive,
  },
  warningMessage: {
    fontSize: 13,
    color: colors.color_destructive,
    marginTop: 8,
  },
  usageNote: {
    fontSize: 13,
    lineHeight: 19,
    color: colors.text_color_secondary,
    marginTop: 8,
  },
  learnMoreContainer: {
    alignItems: 'center',
    marginTop: 12,
  },
  learnMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  learnMoreButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  buttonsContainer: {
    gap: 10,
  },
});

function CreditsRequiredCard({ requestCost, holdCost, fileEstimate = false, hideRefillButton, onClose, isPlus: isPlusProp }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);
  const [isOpen, setIsOpen] = useState(false);

  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable);
  const isPlus = isPlusProp ?? useAuthStore(state => state.user?.flags?.isPlus);
  const handleUpgrade = useCallback(() => {
    setIsOpen(false)
    onClose && onClose();
    openModal('PricingModal', {
      title: "Get More Credits",
      subtitle: `Upgrade to Plus for free default model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`
    });
  }, [openModal, onClose]);

  const handleBuyCredits = useCallback(() => {
    setIsOpen(false)
    onClose && onClose();
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal, onClose]);

  const handleLearnMore = useCallback(() => {
    setIsOpen(false);
    onClose && onClose();
    openModal('CreditsInfoModal');
  }, [openModal, onClose]);

  const { estimated, reserved, hasHold } = resolveComposerCreditQuote(requestCost, holdCost);
  const hasInsufficientCredits = reserved > totalAvailableCredits;

  return (
    <View style={styles.dropdownContent}>
      <View style={styles.header}>
        <View style={styles.creditsIconContainer}>
          <Icon 
            name="coins" 
            size={20}
            color={colors.text_color_primary}
          />
        </View>
        <View style={styles.headerTextContainer}>
          <Text style={styles.creditsNumber}>
            {formatCreditBalance(totalAvailableCredits)} credits
          </Text>
          <Text style={styles.title}>
            Available Balance
          </Text>
        </View>
      </View>

      <View style={styles.infoSection}>
        {estimated === 0 && reserved === 0 ? (
          <View style={styles.costContainer}>
            <Text style={styles.costHint}>
              No credits required, this request is free!
            </Text>
          </View>
        ) : (
          <View style={styles.costStack}>
            <View style={[
              styles.costContainer,
              hasInsufficientCredits && !hasHold && styles.costContainerWarning
            ]}>
              <View style={styles.costRow}>
                <View style={styles.costCopy}>
                  <Text style={styles.costLabel}>Estimated total</Text>
                  <Text style={styles.costHint}>
                    {fileEstimate
                      ? 'Includes file content, likely edits, reasoning, and additional model steps.'
                      : 'Based on the model, conversation, and expected reply.'}
                  </Text>
                </View>
                <Text style={styles.costValue}>
                  ~{formatCreditBalance(estimated)}
                </Text>
              </View>
            </View>
            {hasHold && (
              <View style={[
                styles.costContainer,
                hasInsufficientCredits && styles.costContainerWarning
              ]}>
                <View style={styles.costRow}>
                  <View style={styles.costCopy}>
                    <Text style={styles.costLabel}>Initial temporary hold</Text>
                    <Text style={styles.costHint}>
                      Credits reserved to start. The hold may increase as work continues. Unused credits are released.
                    </Text>
                  </View>
                  <Text style={[
                    styles.costValue,
                    hasInsufficientCredits && styles.costValueWarning
                  ]}>
                    {formatCreditBalance(reserved)}
                  </Text>
                </View>
              </View>
            )}
            <Text style={styles.usageNote}>
              {fileEstimate ? `${FILE_CREDIT_NOTE} ` : ''}
              The estimate and hold are not spending limits. The final charge is based on usage.
            </Text>
          </View>
        )}
        {hasInsufficientCredits && (
          <Text style={styles.warningMessage}>
            You need {formatCreditBalance(reserved - totalAvailableCredits)} more credits to send this
          </Text>
        )}
      </View>

      {/* Plus and short on credits: lay out every choice, not just the store. */}
      {isPlus && hasInsufficientCredits && (
        <View style={{ marginBottom: 12 }}>
          <PlusOutOfCreditsOptions
            compact
            showPacks={false}
            source="composer_credits_card"
            onBeforeAction={() => { setIsOpen(false); onClose && onClose(); }}
          />
        </View>
      )}

      {!hideRefillButton && !(isPlus && hasInsufficientCredits) && (
        <View style={styles.buttonsContainer}>
          {isPlus ? (
            <Button
              variant="primary"
              text="Get more credits"
              onPress={handleBuyCredits}
              isFullWidth
            />
          ) : (
            <>
              <Button
                variant="plus"
                text="Upgrade to Plus"
                prependIcon="sparkles"
                onPress={handleUpgrade}
                isFullWidth
              />
              <Button
                variant="secondary"
                text="Buy Credits"
                prependIcon="coins"
                onPress={handleBuyCredits}
                isFullWidth
              />
            </>
          )}
        </View>
      )}

      <View style={styles.learnMoreContainer}>
        <Pressable
          onPress={handleLearnMore}
          style={({ hovered }) => [
            styles.learnMoreButton,
            hovered && styles.learnMoreButtonHovered
          ]}
        >
          <Icon 
            name="circle-question" 
            size={13} 
            color={colors.text_color_tertiary} 
          />
          <Text size="s" color="tertiary">
            What are credits?
          </Text>
        </Pressable>
      </View>
    </View>
  );
} 

export default React.memo(CreditsRequiredCard);
